import { isFormFieldFocusable } from './isFormFieldFocusable';
import { withOpenOnFocusSuppressed } from './openOnFocusSuppression';

const FOCUS_WAIT_TIMEOUT_MS = 10_000;

// A resolved focus target: the field wrapper (for tab/context) and the concrete
// element that should receive focus inside it.
export type FormFocusTarget = {
  wrapper: HTMLElement;
  focusable: HTMLElement;
};

export type FocusFormFieldOptions = {
  // Abort before focusing — e.g. focus has already moved into the form by another
  // action. Checked on every attempt so a deferred retry can't steal focus later.
  shouldSkip?: () => boolean;
  // Suppress controls that open an overlay on focus (e.g. reference quicksearch)
  // while applying focus. Used by auto-focus so opening the form doesn't pop a panel.
  suppressOpenOnFocus?: boolean;
};

// Applies focus to an element. With suppression on, the synchronous focus event
// runs inside the open-on-focus suppression window.
type FocusApplier = (el: HTMLElement) => void;

const watchUntilFocusable = (
  focusable: HTMLElement,
  focusEl: FocusApplier
): void => {
  const attrObserver = new MutationObserver(() => {
    if (!isFormFieldFocusable(focusable)) {
      return;
    }
    attrObserver.disconnect();
    parentObserver.disconnect();
    focusEl(focusable);
  });

  attrObserver.observe(focusable, {
    attributes: true,
    attributeFilter: ['disabled', 'readonly'],
  });

  const parentObserver = new MutationObserver(() => {
    if (!isFormFieldFocusable(focusable)) {
      return;
    }
    attrObserver.disconnect();
    parentObserver.disconnect();
    focusEl(focusable);
  });

  parentObserver.observe(document.body, {
    attributes: true,
    subtree: true,
    attributeFilter: ['style', 'class', 'hidden'],
  });

  window.setTimeout(() => {
    attrObserver.disconnect();
    parentObserver.disconnect();
  }, FOCUS_WAIT_TIMEOUT_MS);
};

// If the wrapper lives in an inactive PrimeReact TabPanel (renderActiveOnly={false}
// keeps it in DOM but applies .p-hidden), click the matching nav link to switch
// tabs, wait for .p-hidden removal, then focus.
const trySwitchHiddenTab = (
  wrapper: HTMLElement,
  focusable: HTMLElement,
  focusEl: FocusApplier
): boolean => {
  const hiddenPanel = wrapper.closest<HTMLElement>('.p-tabview-panel.p-hidden');
  if (!hiddenPanel) {
    return false;
  }

  const panelsContainer = hiddenPanel.parentElement;
  const tabView = hiddenPanel.closest<HTMLElement>('.p-tabview');
  if (!panelsContainer || !tabView) {
    return false;
  }

  const panelIndex = Array.from(panelsContainer.children).indexOf(hiddenPanel);
  const navLinks = tabView.querySelectorAll<HTMLElement>(
    '.p-tabview-nav .p-tabview-nav-link'
  );
  const navLink = navLinks[panelIndex];
  if (!navLink) {
    return false;
  }

  const classObserver = new MutationObserver(() => {
    if (hiddenPanel.classList.contains('p-hidden')) {
      return;
    }
    classObserver.disconnect();

    if (isFormFieldFocusable(focusable)) {
      focusEl(focusable);
    } else {
      // Tab visible but focusable still disabled/hidden — fall back to attr/parent watch.
      watchUntilFocusable(focusable, focusEl);
    }
  });

  classObserver.observe(hiddenPanel, {
    attributes: true,
    attributeFilter: ['class'],
  });
  window.setTimeout(() => classObserver.disconnect(), FOCUS_WAIT_TIMEOUT_MS);

  navLink.click();
  return true;
};

const focusWhenReady = (
  wrapper: HTMLElement,
  focusable: HTMLElement,
  focusEl: FocusApplier
): void => {
  if (trySwitchHiddenTab(wrapper, focusable, focusEl)) {
    return;
  }

  if (isFormFieldFocusable(focusable)) {
    focusEl(focusable);
    return;
  }

  watchUntilFocusable(focusable, focusEl);
};

// Shared form-focus engine. Resolves a target via `resolveTarget` and focuses it,
// switching hidden tabs and waiting for the element to become focusable as needed.
// If the target is not in the DOM yet, watches the body until it appears (e.g. the
// form is still mounting after a fetch). Callers differ only in what triggers the
// focus and how the target is resolved.
export const focusFormField = (
  resolveTarget: () => FormFocusTarget | null,
  options: FocusFormFieldOptions = {}
): void => {
  const { shouldSkip, suppressOpenOnFocus } = options;

  const focusEl: FocusApplier = suppressOpenOnFocus
    ? (el) => withOpenOnFocusSuppressed(() => el.focus())
    : (el) => el.focus();

  const tryFocus = (): boolean => {
    // Treat a skip as done so the body observer stops watching.
    if (shouldSkip?.()) {
      return true;
    }

    const target = resolveTarget();
    if (!target) {
      return false;
    }

    focusWhenReady(target.wrapper, target.focusable, focusEl);
    return true;
  };

  if (tryFocus()) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (tryFocus()) {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    }
  });
  // Watch insertions AND visibility/enablement changes: a field can already be in
  // the DOM but unfocusable until an ancestor flips (e.g. a tab section becoming
  // visible toggles `display` on a wrapper — a style change, not an insertion).
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden', 'disabled', 'readonly'],
  });

  const timeoutId = window.setTimeout(() => {
    observer.disconnect();
  }, FOCUS_WAIT_TIMEOUT_MS);
};
